import type { Location, Patrol } from '@local/common-lib';
import { isUlid, locationContract } from '@local/common-lib';
import {
  ArrowBack as CancelIcon,
  GpsOff as LocationUnavailableIcon,
  PlayArrow as StartIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import type { Theme } from '@mui/material';
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  InputAdornment,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { FormikProps } from 'formik';
import { DateTime, Duration } from 'luxon';
import type { FC } from 'react';
import { useMemo } from 'react';
import type { Contract } from 'validata';
import {
  isArray,
  isNumber,
  isString,
  maybeObject,
  maybeString,
} from 'validata';
import { FetchOneOfAll } from '../../../components/fetch/fetch-one.js';
import { FormikCheckList } from '../../../components/formik-fields/check-list.js';
import { FormikLocationAddressField } from '../../../components/formik-fields/location-address-field.js';
import { FormikNumberField } from '../../../components/formik-fields/number-field.js';
import { FormikSelect } from '../../../components/formik-fields/select.js';
import { FormikSwitch } from '../../../components/formik-fields/switch.js';
import { FormikTextField } from '../../../components/formik-fields/text-field.js';
import { BottomActionBar } from '../../../components/page-layout/bottom-action-bar.js';
import { CenterLayout } from '../../../components/page-layout/index.js';
import {
  guestRenderer,
  personRenderer,
  roundDownFiveMinutes,
  roundTimeToMinute,
} from './view-utils.js';

export interface StartPatrolForm {
  guestsPresent: string[]; // ids
  location?: Location;
  notes: string;
  patrollersPresent: string[]; // ids
  policeEventNumber?: string;
  started?: string;
  vehicle: boolean;
  vehicleRego: string;
  vehicleStartOdometer?: number;
}

const startTimes = (
  selected: string | undefined,
  patrolStart: DateTime,
  patrolDuration: Duration,
): JSX.Element[] => {
  const now = roundTimeToMinute(DateTime.local());
  const selectedDateTime = selected
    ? roundTimeToMinute(DateTime.fromISO(selected))
    : undefined;
  const min = roundDownFiveMinutes(now < patrolStart ? now : patrolStart);
  const patrolFinish = patrolStart.plus(patrolDuration);
  const max = roundDownFiveMinutes(
    now > patrolStart ? (now > patrolFinish ? patrolFinish : now) : patrolStart,
  );

  const times = [];
  for (
    let time = min.minus({ minutes: 15 });
    time <= max.plus({ minutes: 5 });
    time = time.plus({ minutes: 5 })
  ) {
    times.push(time);
  }
  if (
    selectedDateTime &&
    !times.some((time) => time.toMillis() === selectedDateTime.toMillis())
  ) {
    times.push(selectedDateTime);
  }
  if (!times.some((time) => time.toMillis() === now.toMillis())) {
    times.push(now);
  }

  times.sort((a, b) => a.toMillis() - b.toMillis());
  return times.map((time) => (
    <MenuItem key={time.toISO()} value={time.toLocal().toISO()}>
      {time.toLocal().toFormat('t')}
    </MenuItem>
  ));
};

export const startPatrolFormContractNoVehicle: Contract<
  Omit<StartPatrolForm, 'vehicle' | 'vehicleRego' | 'vehicleStartOdometer'>
> = {
  guestsPresent: isArray(isUlid()),
  location: maybeObject(locationContract),
  notes: isString(),
  patrollersPresent: isArray(isUlid()),
  policeEventNumber: isString(),
  started: maybeString(),
};

export const startPatrolFormContract: Contract<
  Omit<StartPatrolForm, 'vehicle'>
> = {
  ...startPatrolFormContractNoVehicle,
  vehicleRego: isString({
    maxLength: 6,
    minLength: 1,
    regex: /^[0-9a-zA-Z]+$/,
  }),
  vehicleStartOdometer: isNumber({ min: 0 }),
};

interface ViewProps<T> {
  locationAvailable: boolean;
  onRecentVehicleClick: (vehicle: Patrol.PatrolVehicleInfo) => void;
  onRecentVehicleDelete: (vehicle: Patrol.PatrolVehicleInfo) => void;
  patrol: T;
  recentVehicles: Patrol.PatrolVehicleInfo[];
  refresh: () => void;
  startPatrolForm: FormikProps<StartPatrolForm>;
}

export const StartPatrolView: FC<ViewProps<Patrol.Type>> = ({
  locationAvailable,
  patrol,
  refresh,
  startPatrolForm,
  onRecentVehicleClick,
  onRecentVehicleDelete,
  recentVehicles,
}) => {
  const smallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const expectedPatrollers = useMemo(() => {
    const patrollers = patrol.patrollers ?? {};
    return Object.keys(patrollers)
      .filter((patrollerId) => {
        const assignment = patrollers[patrollerId];
        return (
          assignment !== undefined &&
          !assignment.declined &&
          !assignment.removed
        );
      })
      .map((patrollerId) => ({
        display: (
          <FetchOneOfAll
            dataType="person"
            id={patrollerId}
            renderFactory={personRenderer}
          />
        ),
        id: patrollerId,
      }));
  }, [patrol]);

  const expectedGuests = useMemo(() => {
    const guests = patrol.guests || {};

    return Object.keys(guests)
      .filter((guestId) => {
        const assignment = guests[guestId];
        return assignment !== undefined && !assignment.removed;
      })
      .map((guestId) => ({
        display: guestRenderer(guests[guestId]),
        id: guestId,
      }));
  }, [patrol]);

  // view
  return (
    <form
      onReset={startPatrolForm.handleReset}
      onSubmit={startPatrolForm.handleSubmit}
      style={{
        paddingBottom: smallScreen
          ? 'calc(152px + env(safe-area-inset-bottom, 0px))'
          : 'calc(96px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <CenterLayout>
        <Stack direction="column" spacing={3}>
          {!locationAvailable ? (
            <Alert
              color="warning"
              icon={<LocationUnavailableIcon />}
              onClick={() => refresh()}
              sx={{ cursor: 'pointer', mb: { md: 3, xs: 1 } }}
              variant="filled"
            >
              This page has not been granted access location. Please grant
              access in the browser settings.
            </Alert>
          ) : null}
          <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant={smallScreen ? 'h6' : 'h4'}>
                Start Patrol
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardHeader
              subheader="Before starting the patrol, confirm the roll call. Tick each patroller and guest who is present, and untick anyone who has not shown up."
              title="Roll Call"
            />
            <CardContent>
              <Collapse
                in={startPatrolForm.values.patrollersPresent.length < 1}
              >
                <Alert severity="warning" sx={{ mb: 2 }}>
                  At least one patroller must be present to start a patrol
                </Alert>
              </Collapse>
              <Collapse
                in={
                  startPatrolForm.values.patrollersPresent.length >= 1 &&
                  startPatrolForm.values.patrollersPresent.length +
                    startPatrolForm.values.guestsPresent.length <
                    2
                }
              >
                <Alert severity="warning" sx={{ mb: 2 }}>
                  At least two people must be present to start a patrol
                </Alert>
              </Collapse>

              <Typography color="text.primary" variant="h6">
                Assigned patrollers
              </Typography>
              <FormikCheckList
                formik={startPatrolForm}
                id="patrollersPresent"
                items={expectedPatrollers}
              />
            </CardContent>
            {expectedGuests.length ? (
              <CardContent>
                <Typography color="text.primary" variant="h6">
                  Guests
                </Typography>
                <FormikCheckList
                  formik={startPatrolForm}
                  id="guestsPresent"
                  items={expectedGuests}
                />
              </CardContent>
            ) : undefined}
          </Card>
          <Card>
            <CardHeader
              action={
                <FormikSwitch formik={startPatrolForm} id="vehicle" label="" />
              }
            />
            <CardContent>
              <Collapse in={!startPatrolForm.values.vehicle}>
                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  A vehicle is not being used for this patrol.
                </Typography>
              </Collapse>
              <Collapse in={startPatrolForm.values.vehicle}>
                <Stack direction="column" spacing={0}>
                  {recentVehicles.length ? (
                    <Stack direction="column" spacing={2}>
                      <Typography
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                        variant="body2"
                      >
                        Select previous vehicle or enter details below
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ overflowX: 'auto', pb: 2 }}
                      >
                        {recentVehicles.map((vehicle) => (
                          <Chip
                            key={vehicle.rego}
                            label={vehicle.rego}
                            onClick={() => onRecentVehicleClick(vehicle)}
                            onDelete={() => onRecentVehicleDelete(vehicle)}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  ) : undefined}
                  <Stack direction={{ md: 'row', xs: 'column' }} spacing={3}>
                    <FormikTextField
                      formik={startPatrolForm}
                      id="vehicleRego"
                      inputProps={{
                        sx: { textTransform: 'uppercase' },
                      }}
                      label="Rego"
                    />
                    <FormikNumberField
                      formik={startPatrolForm}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">km</InputAdornment>
                        ),
                      }}
                      id="vehicleStartOdometer"
                      label="Start Odometer"
                      type="number"
                    />
                  </Stack>
                </Stack>
              </Collapse>
            </CardContent>
          </Card>
          <Card>
            <CardHeader title="Details" />
            <CardContent>
              <Stack direction={{ md: 'row', xs: 'column' }} spacing={3}>
                <Stack direction="column" flexGrow={1} spacing={3}>
                  <FormikSelect
                    formik={startPatrolForm}
                    id="started"
                    label="Start Time"
                  >
                    {startTimes(
                      startPatrolForm.values.started,
                      DateTime.fromJSDate(patrol.when),
                      Duration.fromObject({ hours: patrol.hours }),
                    )}
                  </FormikSelect>
                  <FormikTextField
                    formik={startPatrolForm}
                    id="policeEventNumber"
                    inputProps={{
                      sx: { textTransform: 'uppercase' },
                    }}
                    label="Police Event Number"
                  />
                </Stack>
                <Stack direction="column" flexGrow={1} spacing={3}>
                  <FormikTextField
                    formik={startPatrolForm}
                    id="notes"
                    label="Notes"
                    multiline
                    rows={4}
                  />
                </Stack>
              </Stack>
            </CardContent>
            <CardContent>
              <FormikLocationAddressField
                formik={startPatrolForm}
                id="location"
                label="Location"
              />
            </CardContent>
          </Card>
          <BottomActionBar>
            <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
              <LoadingButton
                color="primary"
                fullWidth
                loading={startPatrolForm.isSubmitting}
                onClick={startPatrolForm.handleReset}
                startIcon={<CancelIcon />}
              >
                I'll start later
              </LoadingButton>
              <LoadingButton
                color="primary"
                fullWidth
                loading={startPatrolForm.isSubmitting}
                startIcon={<StartIcon />}
                type="submit"
                variant="contained"
              >
                Start Now
              </LoadingButton>
            </Stack>
          </BottomActionBar>
        </Stack>
      </CenterLayout>
    </form>
  );
};
